import { useContext } from "react";
import { navigate } from "gatsby";
import PropTypes from "prop-types";

import { CartContext } from "@context/CartProvider";
import { useAuth } from "@context/AuthProvider";
import { LuxeContext } from "@context/LuxeProvider";
import { useProductData2 } from "@context/products";
import { useCurrentCountryCode, useProductPricing } from "@hooks/usePrices";
import { cn } from "@helpers/general";
import { getLuxeGiftInfo } from "@helpers/luxe";
import { ComponentType } from "@constants/ComponentType";

import { ALButtonV2 } from "@components/ALComponents";
import LuxeGiftCountdown from "./LuxeGiftCountdown";
import { HandleType } from "@constants/HandleType";

function LuxeGiftProduct({ componentType }) {
  const { itemsInCart, toggleCart } = useContext(CartContext);
  const { luxe } = useContext(LuxeContext);

  const countryCode = useCurrentCountryCode();
  const { accountDetails } = useAuth();

  const luxeGiftPeriod = getLuxeGiftInfo(luxe, accountDetails?.tags);
  const isLuxeGiftUnlocked = luxeGiftPeriod.status === "unlocked";

  const {
    data: [product],
  } = useProductData2([HandleType.LUXE_RETENTION_GIFT], "short", countryCode);

  const {
    products: [productData],
  } = useProductPricing({
    products: [product],
  });

  if (!productData) {
    return null;
  }

  const handleButtonClick = () => {
    if (itemsInCart === 0) {
      navigate("/collections/bestsellers");
    } else {
      toggleCart(true);
    }
  };
  return (
    <div
      className={cn("relative rounded-[4px] bg-white desktop:flex-1", {
        "opacity-[0.5]": !isLuxeGiftUnlocked && componentType !== ComponentType.ANTI_CHURN_POPUP,
        " p-[15px] desktop:p-[20px]": componentType !== ComponentType.ANTI_CHURN_POPUP,
      })}
    >
      <div className="flex gap-[15px]">
        <img
          className="w-[108px] desktop:w-[125px] h-[135px] desktop:h-[150px] object-cover"
          src={productData?.node?.images[0]?.url}
          alt={productData?.node?.images[0]?.altText}
        />
        <div className="flex flex-col justify-between">
          <div className="typo-body bold">
            <p>{productData?.node.title.split("-")[1]}</p>
            <p className="microtext text-dark-grey mt-[1px] desktop:mt-[3px]">
              {productData?.node.title.split("-")[0]}
            </p>
            <div className="flex gap-[3px] mt-[5px]">
              <span className="strikethrough">{productData?.formattedFullPrice}</span>
              <span className="text-secondary">FREE</span>
            </div>
            {componentType === ComponentType.ANTI_CHURN_POPUP && (
              <LuxeGiftCountdown componentType={componentType} />
            )}
          </div>

          {componentType !== ComponentType.ANTI_CHURN_POPUP && (
            <p className="typo-micro text-secondary">
              Your free gift will be automatically added when another item is in your cart.
            </p>
          )}
        </div>
      </div>
      {isLuxeGiftUnlocked && componentType !== ComponentType.ANTI_CHURN_POPUP && (
        <ALButtonV2
          fullWidth
          size="large"
          className="mt-[15px]"
          onClick={handleButtonClick}
          data-is-open-cart-button
        >
          {itemsInCart === 0 ? "Shop Best Sellers" : "View Cart & Check Out"}
        </ALButtonV2>
      )}
    </div>
  );
}

LuxeGiftProduct.propTypes = {
  componentType: PropTypes.string,
};

export default LuxeGiftProduct;
